import React from 'react'
import { Link } from 'react-router-dom'
import { type GridValidRowModel } from '@mui/x-data-grid'
import { FieldType } from './types'
import Box from '@mui/material/Box'
import CellWarning from './CellTypes/CellWarning'
import ROUTES from '../../consts/paths'
import { isArray } from 'lodash'
import LeadText from './CellTypes/LeadText'
import { Typography } from '@mui/material'
import CellBadge from './CellTypes/CellBadge'
import MultiBadge from './CellTypes/MultiBadge'
import { grey } from '../../colors'
import { formatDateByUserTimeZone } from '../../helpers/formatDateByUserTimeZone'
import TextList from './CellTypes/TextList'
import ObjectList from './CellTypes/ObjectList'
import ChipsForAttributes, { type DataItem } from './CellTypes/ChipsForAttributes'

const flattenKeys = (obj: Record<string, any>, parentKey = ''): Record<string, string | null> => {
  let result: Record<string, any> = {}
  for (const key in obj) {
    const newKey = parentKey !== '' ? `${parentKey}.${key}` : key
    if (isArray(obj[key])) {
      result[key] = obj[key]
      continue
    }
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      const nestedKeys = flattenKeys(obj[key], newKey)
      result = { ...result, ...nestedKeys }
      continue
    } else {
      result[newKey] = obj[key]
      continue
    }
  }
  return result
}

const toCamelCaseRemoveDotAlso = (str: string): string => {
  return str
    .split(/[._]/)
    .map((word, i) =>
      i === 0
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join('')
}

const toCamelCase = (str: string): string => {
  return str.replace(/_([a-zA-Z])/g, (_, match) => match.toUpperCase())
    .replace(/_(\d)/g, '_$1')
}

export const generateCell = <T,>(colDef: GridValidRowModel, row: Record<string, any>, setSelectedRow: any, setActionModal: any, columnIndex: number, overrides: Record<string, (a: T) => JSX.Element>): JSX.Element => {
  const { data } = row

  const camelcaseField = toCamelCase(colDef.field)

  const warningMessageObj = data?.warningMessage?.[toCamelCaseRemoveDotAlso(colDef.field)]
  const isFirstColumn = columnIndex === 0

  const flattenedData = flattenKeys(data)
  const getFieldData = (): { value: string | string[], link: string } => {
    const value: string = ''
    const link = data?.divisionId ?? data?.sectionId

    if (colDef.field === 'children' && data?.children?.length > 0) {
      return {
        value: data?.children,
        link
      }
    }
    const matchedValue = Object.keys(flattenedData).find(key => key === camelcaseField)

    if (matchedValue !== undefined) {
      const value = flattenedData?.[matchedValue] ?? ''
      return {
        value,
        link
      }
    }

    return {
      value,
      link
    }
  }

  const linkDisabledLinkStyle = {
    textDecoration: 'none',
    cursor: 'pointer'
  }

  const { value, link = '' } = getFieldData()

  const override = overrides[camelcaseField]
  if (override !== undefined && override !== null) {
    return overrides[camelcaseField](flattenedData as T)
  }

  const commonStyles = (): React.CSSProperties => ({
    fontSize: '14px',
    color: grey[850],
    fontWeight: 400
  })

  switch (colDef.type) {
    case FieldType.TEXT: {
      return (
        <>
          {warningMessageObj && <CellWarning keyType={warningMessageObj.key} message={warningMessageObj.value} />}
          {isFirstColumn
            ? <LeadText>{value}</LeadText>
            : <Typography sx={commonStyles}>{value}</Typography>
          }
        </>
      )
    }

    case FieldType.LINK: {
      return (
        <Link to={`${ROUTES.REPORTING_HIERARCHY}/${link}`}
          style={linkDisabledLinkStyle}
        >
          {warningMessageObj && <CellWarning keyType={warningMessageObj.key} message={warningMessageObj.value} />}
          {isFirstColumn
            ? <LeadText>{value}</LeadText>
            : <Typography sx={commonStyles}>{value}</Typography>
          }
        </Link>
      )
    }

    case FieldType.LINKED_ENTITY: {
      return (
        <>
          {warningMessageObj && <CellWarning keyType={warningMessageObj.key} message={warningMessageObj.value} />}
          {isFirstColumn
            ? <LeadText>{value}</LeadText>
            : <Typography sx={commonStyles}>{value}</Typography>
          }
        </>
      )
    }

    case FieldType.CHIP: {
      // Check if the first element in the value array is an object.
      // If so, render the ChipsForAttributes component, which is likely used to display attributes in chip form.
      if (Array.isArray(value) && typeof value[0] === 'object') {
        return <ChipsForAttributes data={value as unknown as DataItem[]} />
      }

      // Check if the value is an array.
      // If value is an array, render a MultiBadge component that can handle multiple items.
      if (isArray(value)) {
        return <MultiBadge list={value} maxVisible={2} />
      }

      //  Treat the value as a single value and render a CellBadge component.
      return <CellBadge label={value} />
    }

    case FieldType.TEXT_LIST: {
      return <TextList list={value as string[]} />
    }
    case FieldType.OBJECT_LIST: {
      return <ObjectList list={value as unknown as Array<Record<string, any>> } />
    }
    case FieldType.DATE: {
      const isValidDate = (dateString: string): boolean => !isNaN(Date.parse(dateString))
      const formatedDate = isValidDate(value as string) && formatDateByUserTimeZone(value as string, 'MM/dd/yyyy @ h:mm a')
      return <Typography sx={commonStyles} >{formatedDate}</Typography>
    }
    case FieldType.NUMBER: {
      return (
        isFirstColumn
          ? <LeadText >{value} </LeadText>
          : <Typography sx={commonStyles} >{value}</Typography>
      )
    }
    default: {
      return <Box component="span">  {typeof value === 'string' ? value : ''}</Box>
    }
  }
}
